/**
 * Form Controls Design System Tokens
 * 
 * SOURCE OF TRUTH for Admin/Account pages:
 * - Simple Select (Radix UI): OpportunityFilters.tsx
 * - Input fields: OpportunityFilters.tsx
 * - Searchable Command (cmdk): FilterPublicationLocaleSelector.tsx
 * 
 * EXCEPTION - Footer Selectors (DO NOT MODIFY):
 * - LanguageSelector.tsx - ghost variant with custom styling
 * - LocationSelector.tsx - uses LocationPicker with ghost variant
 * 
 * ─────────────────────────────────────────────────────────────────
 * FOCUS STATE STANDARDS
 * ─────────────────────────────────────────────────────────────────
 * 
 * Form inputs (Input, Textarea, Select, Checkbox, Radio, Toggle, Badge):
 *   → Use FORM_CONTROLS.focus.default (subtle border change)
 *   → Pattern: focus-visible:outline-none focus-visible:border-ring
 * 
 * High-visibility interactive elements (Sheet close, Toast actions, Dialog close):
 *   → Use FORM_CONTROLS.focus.ring (thick 2px ring)
 *   → Pattern: focus-visible:outline-none focus-visible:border-ring
 * 
 * Switch/Slider (circular controls):
 *   → Use focus-visible:ring-2 focus-visible:ring-primary
 *   → The ring wraps the circular shape better than border
 * 
 * Tab navigation (TabsTrigger, TabsContent):
 *   → Keep ring-offset for keyboard navigation visibility
 * 
 * CODEMOD: Run `focus-ring-consistency` to normalize thick rings to subtle borders
 * ─────────────────────────────────────────────────────────────────
 */

export const FORM_CONTROLS = {
  // ─────────────────────────────────────────────────────────────────
  // HEIGHT VARIANTS
  // Standardized input/button heights across all form controls
  // ─────────────────────────────────────────────────────────────────
  height: {
    default: "h-10",      // 40px - standard forms
    compact: "h-9",       // 36px - dense forms  
    inline: "h-8",        // 32px - inline editing
    inlineSm: "h-7",      // 28px - very compact
  },

  // ─────────────────────────────────────────────────────────────────
  // VALIDATION STATES
  // Use for consistent error/success styling across all inputs
  // ─────────────────────────────────────────────────────────────────
  validation: {
    default: "border-input",
    error: "border-destructive focus-visible:ring-destructive/20 bg-destructive/5",
    success: "border-success focus-visible:ring-success/20 bg-success/5",
    warning: "border-warning focus-visible:ring-warning/20 bg-warning/5",
    errorBorder: "border-destructive",
    successBorder: "border-success",
    cardError: "border-destructive ring-1 ring-destructive",
  },

  // ─────────────────────────────────────────────────────────────────
  // ICON POSITIONING
  // For left/right icons in inputs and buttons
  // ─────────────────────────────────────────────────────────────────
  icon: {
    left: "absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10",
    right: "absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-10",
    default: "h-4 w-4 text-muted-foreground",
    sm: "h-3.5 w-3.5 text-muted-foreground",
  },

  // ─────────────────────────────────────────────────────────────────
  // FOCUS STATES
  // Alternative focus styles for specific contexts
  // ─────────────────────────────────────────────────────────────────
  focus: {
    /** Default focus - subtle border change (no ring) */
    default: "focus-visible:outline-none focus-visible:border-ring",
    /** Ring focus - thick 2px ring for high visibility contexts */
    ring: "focus-visible:outline-none focus-visible:border-ring",
    error: "focus-visible:border-destructive",
    success: "focus-visible:border-success",
  },

  // ─────────────────────────────────────────────────────────────────
  // SELECT TRIGGER WIDTHS (Radix UI Select)
  // Pattern from: OpportunityFilters.tsx
  // ─────────────────────────────────────────────────────────────────
  select: {
    trigger: {
      xxs: "min-w-[80px] sm:w-[100px]",   // very compact (limit selectors)
      xs: "min-w-[100px] sm:w-[120px]",   // compact filters
      sm: "min-w-[120px] sm:max-w-[200px] w-auto",   // status, timeline — flexible to fit translated labels
      md: "min-w-[120px] sm:max-w-[220px] w-auto",   // service type
      lg: "min-w-[120px] sm:max-w-[240px] w-auto",   // wider options
      xl: "min-w-[160px] sm:w-[240px]",   // very wide (language selectors)
      xxl: "min-w-[280px] sm:w-[320px]",  // very wide (AI model selectors)
      full: "w-full",                      // full-width form fields
      // Responsive full-to-fixed patterns
      fullToXs: "w-full sm:w-[120px]",
      fullToSm: "w-full sm:w-[140px]",
      fullToMd: "w-full sm:w-[160px]",
      fullToLg: "w-full sm:w-[180px]",
      fullToXl: "w-full sm:w-[240px]",    // wide language selectors
    },
    // SelectItem styling
    item: "cursor-pointer",
    // SelectItem with left padding for alignment (country/state selectors)
    itemAligned: "pl-3 text-left cursor-pointer",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // FILTER FIELD STYLING (unified appearance)
  // All filter controls should share these base styles
  // ─────────────────────────────────────────────────────────────────
  filterField: {
    wrapper: "space-y-2",
    label: "text-sm font-medium",
    // Unified trigger appearance for buttons/popovers - uses input-interactive for consistent hover
    trigger: "w-full h-10 justify-between bg-background input-interactive",
    // Compact trigger for toolbar filters (use with size="sm" and custom widths)
    triggerSm: "justify-between bg-background input-interactive",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // INPUT FIELDS
  // Pattern from: OpportunityFilters.tsx
  // ─────────────────────────────────────────────────────────────────
  input: {
    search: "pl-9 bg-background",          // with search icon + consistent bg
    withIcon: "pl-9",                      // generic icon padding
    default: "",                           // standard input
    // Title/editor inputs (borderless, bottom border only)
    title: "h-auto py-4 border-0 border-b rounded-none focus-visible:ring-0 focus-visible:border-primary px-0 bg-transparent text-2xl sm:text-3xl md:text-3xl font-bold",
    // Compact inline editing (h-7, sidebar renaming, quick edit)
    inlineSm: "h-7 text-sm flex-1",
    // Standard inline editing (h-8, table rows, card edits)
    inline: "h-8 text-sm flex-1",
    // Slug inputs (monospace, compact)
    slug: "h-8 text-sm font-mono flex-1",
    // Compact form inputs (h-8, forms in modals/sidebars)
    compact: "h-8 text-sm",
    // Width variants for standalone search inputs
    width: {
      xs: "w-16",                          // 64px - very compact number inputs
      sm: "w-48",                          // compact search
      md: "w-64",                          // standard search
      lg: "max-w-md",                      // wide search
      full: "w-full",                      // full-width
      responsive: "w-full sm:w-64",        // responsive pattern
    },
  },
  
  // ─────────────────────────────────────────────────────────────────
  // SEARCH INPUT WRAPPER
  // Pattern from: OpportunityFilters.tsx line 45-54
  // ─────────────────────────────────────────────────────────────────
  searchWrapper: {
    container: "relative w-full",
    icon: "absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // COMMAND-BASED SELECTORS (cmdk)
  // Pattern from: FilterPublicationLocaleSelector.tsx
  // CRITICAL: No hover:bg-* - cmdk handles via data-[selected]
  // ─────────────────────────────────────────────────────────────────
  command: {
    popover: "p-0 bg-popover",
    popoverWide: "w-64 p-0 bg-popover",
    // Compact popover - matches trigger width, for simple sort/status selectors
    popoverCompact: "w-[var(--radix-popover-trigger-width)] p-0 bg-popover",
    // Auto-width popover - min matches trigger, expands for longer content
    popoverAutoWidth: "min-w-[var(--radix-popover-trigger-width)] w-auto p-0 bg-popover",
    // Responsive popover - mobile full-width, desktop 300px
    popoverResponsive: "w-[calc(100vw-2rem)] sm:w-[300px] max-w-[300px] p-0 bg-popover",
    trigger: "justify-between",
    // NEVER add hover:bg-muted to CommandItem
    item: "cursor-pointer",
    // No-wrap item for compact dropdowns
    itemNoWrap: "cursor-pointer whitespace-nowrap",
    // Selected state uses muted background (matching header button hover)
    itemSelected: "cursor-pointer bg-muted text-foreground",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // CHECK INDICATORS
  // ─────────────────────────────────────────────────────────────────
  check: {
    icon: "mr-2 h-4 w-4",
    visible: "mr-2 h-4 w-4 opacity-100",
    hidden: "mr-2 h-4 w-4 opacity-0",
    badge: "ml-auto flex-shrink-0 bg-success rounded-full p-0.5",
    badgeIcon: "h-3 w-3 text-success-foreground",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // FILTER ROW LAYOUT
  // Pattern from: OpportunityFilters.tsx
  // ─────────────────────────────────────────────────────────────────
  filterRow: {
    wrapper: "grid grid-cols-2 sm:flex sm:flex-wrap w-full sm:w-auto",
    label: "flex items-center gap-2 text-sm text-muted-foreground",
  },
  
  // ─────────────────────────────────────────────────────────────────
  // BUTTON PATTERNS
  // Common button patterns for filter/form areas
  // ─────────────────────────────────────────────────────────────────
  button: {
    // Collapsible/accordion triggers (full-width, justify-between)
    collapsibleTrigger: "w-full justify-between",
    // Icon-only buttons in toolbars
    iconButton: "shrink-0",
    // Action buttons in table rows (use with TABLES.actionButton for sizing)
    tableAction: "shrink-0",
    // Text buttons with icons (e.g., "View SQL", "Refresh", "Run")
    textButton: "shrink-0",
    // Compact buttons (h-8 height, use with size="sm")
    compact: "h-8",
    // Compact full-width action buttons
    compactFull: "w-full h-8 text-sm",
    // Compact action buttons with text (h-7 with text-xs) - bulk actions, AI generation
    compactAction: "h-7 text-xs",
    // Compact icon-only buttons (h-7 w-7 p-0) - table row actions
    iconCompact: "h-7 w-7 p-0",
    // Touch-friendly icon buttons - extra small (h-6)
    iconTouchXs: "h-6 w-6 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0",
    // Touch-friendly icon buttons - small (h-7)
    iconTouchSm: "h-7 w-7 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0",
    // Touch-friendly icon buttons - standard (h-8)
    iconTouch: "h-8 w-8 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0",
    // Touch-friendly minimal (no explicit size, just touch targets)
    touchMinimal: "min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0",
    // Monospace variable badge button (template variables)
    variableBadge: "h-7 gap-1 font-mono text-xs",
    // Clear selection button (compact with horizontal padding)
    clearSelection: "h-7 px-2",
    // Compact collapsible triggers (h-7 with horizontal padding and gap)
    compactCollapsible: "h-7 px-2 gap-1 text-xs",
  },
} as const;

export type FormControlToken = keyof typeof FORM_CONTROLS;
